import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Container,
  alpha,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AutoAwesome as LogoIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { tokens, hoverUnlessShowingFocus } from '../../theme';

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Features', href: '#features' },
    { label: 'Pricing', href: '/pricing' },
    { label: 'Integrations', href: '/integrations' },
    { label: 'Blog', href: '/blog' },
    { label: 'Help', href: '/help' },
  ];

  const handleNavClick = (href: string) => {
    setMobileMenuOpen(false);
    if (href.startsWith('#')) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      } else {
        // Not on the page that has this anchor — navigate to home + hash
        navigate('/' + href);
      }
    } else {
      navigate(href);
    }
  };

  return (
    <>
      <Box
        component="nav"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          transition: 'all 0.3s ease',
          py: isScrolled ? 1.5 : 2,
          background: isScrolled 
            ? alpha(tokens.background.paper, 0.85) 
            : 'transparent',
          backdropFilter: isScrolled ? 'blur(20px) saturate(180%)' : 'none',
          borderBottom: isScrolled 
            ? `1px solid ${alpha(tokens.border.light, 0.5)}` 
            : 'none',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            {/* Logo */}
            <Box
              component={RouterLink}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                textDecoration: 'none',
                cursor: 'pointer',
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '10px',
                  background: tokens.primary.gradient,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 4px 14px ${alpha(tokens.primary.main, 0.35)}`,
                }}
              >
                <LogoIcon sx={{ fontSize: 24, color: 'white' }} />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  background: tokens.primary.gradient,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                Chairs Up
              </Typography>
            </Box>

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {navLinks.map((link) => (
                  <Button
                    key={link.label}
                    onClick={() => handleNavClick(link.href)}
                    sx={{
                      color: tokens.text.primary,
                      fontWeight: 500,
                      px: 2,
                      py: 1,
                      borderRadius: '10px',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: alpha(tokens.primary.main, 0.08),
                        color: tokens.primary.main,
                      },
                    }}
                  >
                    {link.label}
                  </Button>
                ))}
              </Box>
            )}

            {/* CTA Buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {!isMobile && (
                <Button
                  onClick={() => navigate('/login')}
                  sx={{
                    color: tokens.text.primary,
                    fontWeight: 500,
                    '&:hover': {
                      background: alpha(tokens.primary.main, 0.08),
                    },
                  }}
                >
                  Log In
                </Button>
              )}
              <Button
                variant="contained"
                onClick={() => navigate('/signup')}
                endIcon={!isMobile && <ArrowIcon />}
                sx={{
                  px: { xs: 2, md: 3 },
                  py: 1,
                  fontWeight: 600,
                  borderRadius: '10px',
                  background: tokens.primary.gradient,
                  boxShadow: `0 4px 14px ${alpha(tokens.primary.main, 0.35)}`,
                  '&:hover': {
                    background: tokens.primary.gradient,
                    filter: 'brightness(1.1)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                Start Free
              </Button>

              {/* Mobile Menu Button */}
              {isMobile && (
                <IconButton
                  onClick={() => setMobileMenuOpen(true)}
                  sx={{
                    color: tokens.text.primary,
                    ml: 1,
                  }}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: 320,
            background: tokens.background.paper,
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 4,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '8px',
                  background: tokens.primary.gradient,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <LogoIcon sx={{ fontSize: 20, color: 'white' }} />
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Chairs Up
              </Typography>
            </Box>
            <IconButton onClick={() => setMobileMenuOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Navigation Links */}
          <List sx={{ mb: 3 }}>
            {navLinks.map((link) => (
              <ListItem key={link.label} disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={() => handleNavClick(link.href)}
                  sx={{
                    borderRadius: '12px',
                    py: 1.5,
                    [hoverUnlessShowingFocus]: {
                      background: alpha(tokens.primary.main, 0.08),
                    },
                  }}
                >
                  <ListItemText
                    primary={link.label}
                    primaryTypographyProps={{
                      fontWeight: 500,
                      color: tokens.text.primary,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          {/* CTA Buttons */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                setMobileMenuOpen(false);
                navigate('/signup');
              }}
              sx={{
                py: 1.5,
                fontWeight: 600,
                borderRadius: '12px',
                background: tokens.primary.gradient,
              }}
            >
              Start Free
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setMobileMenuOpen(false);
                navigate('/login');
              }}
              sx={{
                py: 1.5,
                fontWeight: 500,
                borderRadius: '12px',
                borderColor: tokens.border.main,
                color: tokens.text.primary,
              }}
            >
              Log In
            </Button>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;
